import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { readAirValveProperties } from '@/components/map/utils/airValveFeatureProperties';
import type { AirValveFeatureProperties } from '@/components/map/utils/airValveFeatureProperties';
import { toLngLatTuple } from '@/utils/mapUtils';

export interface AirValveInteractionState {
    /** The valve whose detail card should be shown (a pinned click takes priority over hover). */
    activeValve: AirValveFeatureProperties | null;
    /** Geographic [longitude, latitude] — the MapLibre `<Popup>` anchor and the nav deep-links. */
    activeCoords: [number, number] | null;
    /** True when the active valve was clicked (sticky) rather than merely hovered. */
    isPinned: boolean;
    onCardEnter: () => void;
    onCardLeave: () => void;
    clearSelection: () => void;
}

const AIR_VALVE_LAYER_IDS = [ORBIT_LAYER_IDS.airValves, ORBIT_LAYER_IDS.airValvesHighRisk];

/** Stable key for a valve feature — air valves carry no id, so name + coordinate identifies one. */
function valveKey(props: AirValveFeatureProperties, coords: [number, number]): string {
    return `${props.name}|${coords[0].toFixed(6)},${coords[1].toFixed(6)}`;
}

/**
 * Manages hover and click ("pin") state for SAVI air-valve markers, driving a single
 * marker-anchored detail card. Hovering previews a valve; clicking pins it so the card
 * stays open until the user clicks empty space or another valve. Mirrors the POI hover
 * mechanics (mousemove topmost-feature detection, debounced leave). The card's geographic
 * point anchors the AirValvePopup, which the MapLibre `<Popup>` projects and tracks itself.
 *
 * Only attaches listeners when `enabled` (SAVI mode); otherwise it is inert so it never
 * conflicts with the POI interactions on the Find & Fix map.
 */
export function useAirValveInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    enabled: boolean,
): AirValveInteractionState {
    const [activeValve, setActiveValve] = useState<AirValveFeatureProperties | null>(null);
    const [activeCoords, setActiveCoords] = useState<[number, number] | null>(null);
    const [isPinned, setIsPinned] = useState(false);

    const activeKeyRef = useRef<string | null>(null);
    const pinnedRef = useRef(false);
    const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dismissRef = useRef<() => void>(() => {});

    const clearSelection = useCallback(() => {
        dismissRef.current();
    }, []);

    const onCardEnter = useCallback(() => {
        if (leaveTimeoutRef.current !== null) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }
    }, []);

    const onCardLeave = useCallback(() => {
        // A pinned card never dismisses on pointer-out; a hover preview does.
        if (!pinnedRef.current) dismissRef.current();
    }, []);

    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        dismissRef.current = () => {
            leaveTimeoutRef.current = null;
            activeKeyRef.current = null;
            pinnedRef.current = false;
            mlMap.getCanvas().style.cursor = '';
            setActiveValve(null);
            setActiveCoords(null);
            setIsPinned(false);
        };

        const queryValve = (point: maplibregl.Point) => {
            const features = mlMap.queryRenderedFeatures(point, { layers: AIR_VALVE_LAYER_IDS });
            if (!features.length) return null;
            const feature = features[0];
            if (feature.geometry.type !== 'Point') return null;
            const coords = toLngLatTuple(feature.geometry.coordinates);
            if (!coords) return null;
            return { props: readAirValveProperties(feature.properties), coords };
        };

        const showValve = (props: AirValveFeatureProperties, coords: [number, number], pinned: boolean) => {
            activeKeyRef.current = valveKey(props, coords);
            pinnedRef.current = pinned;
            setActiveValve(props);
            setActiveCoords(coords);
            setIsPinned(pinned);
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover-preview creation while the camera moves — a card created
            // mid-drag/zoom/fly would pan with the map. Pinning is a click concern
            // and is unaffected by this guard.
            if (mlMap.isMoving()) return;
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }
            const hit = queryValve(e.point);
            if (!hit) return;
            mlMap.getCanvas().style.cursor = 'pointer';

            const key = valveKey(hit.props, hit.coords);
            // No churn while the cursor stays on the same valve; never override a pin via hover.
            if (key === activeKeyRef.current || pinnedRef.current) return;
            showValve(hit.props, hit.coords, false);
        };

        const handleMouseLeave = () => {
            mlMap.getCanvas().style.cursor = '';
            if (pinnedRef.current) return;
            leaveTimeoutRef.current = setTimeout(() => {
                dismissRef.current();
            }, 150);
        };

        const handleValveClick = (e: maplibregl.MapLayerMouseEvent) => {
            const hit = queryValve(e.point);
            if (!hit) return;
            showValve(hit.props, hit.coords, true);
        };

        // Camera movement dismisses a hover-only preview (cancelling its deferred leave
        // dismissal so it cannot fire late) but never a deliberately pinned card — the
        // pin pans with its anchor and only another feature interaction or an empty
        // click removes it.
        const handleMoveStart = () => {
            if (pinnedRef.current) return;
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
            }
            dismissRef.current();
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        // Dismissal on non-valve clicks (empty space, polygons, clusters) is owned by the
        // global click listener (useMapClickDismissal), which MapView wires to
        // `clearSelection` — this hook only creates the card, never decides emptiness.
        for (const layerId of AIR_VALVE_LAYER_IDS) {
            mlMap.on('mousemove', layerId, handleMouseMove);
            mlMap.on('mouseleave', layerId, handleMouseLeave);
            mlMap.on('click', layerId, handleValveClick);
        }
        mlMap.on('movestart', handleMoveStart);

        return () => {
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }
            for (const layerId of AIR_VALVE_LAYER_IDS) {
                mlMap.off('mousemove', layerId, handleMouseMove);
                mlMap.off('mouseleave', layerId, handleMouseLeave);
                mlMap.off('click', layerId, handleValveClick);
            }
            mlMap.off('movestart', handleMoveStart);
            mlMap.getCanvas().style.cursor = '';
        };
    }, [enabled, isMapLoaded, mapRef]);

    return {
        activeValve,
        activeCoords,
        isPinned,
        onCardEnter,
        onCardLeave,
        clearSelection,
    };
}
